import { useMemo } from "react";
import { Clock } from "lucide-react";

/**
 * ReadTimeSignal
 * Editorial read-time chip that auto-calculates minutes from word count.
 * Reads at 220 wpm (industry average for educated readers on long-form).
 *
 * Usage:
 *   <ReadTimeSignal text={fullArticleText} />
 *   <ReadTimeSignal words={1450} />
 *   <ReadTimeSignal text={text} variant="dark" />          // for dark sections
 *   <ReadTimeSignal text={text} prefix="Essay" />          // "Essay · 4 min read"
 *
 * Audio toggle is scaffolded but hidden by default, pass `audioSrc` later
 * to enable a "Listen instead" link (no audio infrastructure yet, by design).
 */
const WORDS_PER_MIN = 220;

function countWords(text) {
  if (!text) return 0;
  return text
    .toString()
    .replace(/<[^>]*>/g, " ") // strip HTML tags if any
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export default function ReadTimeSignal({
  text,
  words: rawWords,
  prefix,
  variant = "light", // "light" | "dark"
  audioSrc, // optional, when populated, shows the Listen toggle
  className = "",
  testId = "read-time-signal",
}) {
  const minutes = useMemo(() => {
    const w = typeof rawWords === "number" ? rawWords : countWords(text);
    return Math.max(1, Math.round(w / WORDS_PER_MIN));
  }, [text, rawWords]);

  const isDark = variant === "dark";
  const baseColor = isDark ? "text-warm/65" : "text-ash";
  const ruleColor = isDark ? "bg-gold-light/60" : "bg-gold/60";
  const audioColor = isDark
    ? "text-gold-light hover:text-warm"
    : "text-gold hover:text-ink";

  return (
    <div
      className={`inline-flex items-center gap-3 font-mono text-[10px] tracking-[0.22em] uppercase ${baseColor} ${className}`}
      data-testid={testId}
    >
      {prefix ? (
        <>
          <span className={`block w-[18px] h-px ${ruleColor}`} aria-hidden="true" />
          <span className="not-italic">{prefix}</span>
          <span aria-hidden="true">·</span>
        </>
      ) : (
        <span className={`block w-[18px] h-px ${ruleColor}`} aria-hidden="true" />
      )}
      <span className="inline-flex items-center gap-1.5">
        <Clock size={11} strokeWidth={1.6} aria-hidden="true" />
        {minutes} min read
      </span>
      {audioSrc ? (
        <>
          <span aria-hidden="true">·</span>
          <a
            href={audioSrc}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 ${audioColor} transition-colors`}
            data-testid={`${testId}-audio-link`}
          >
            Listen instead →
          </a>
        </>
      ) : null}
    </div>
  );
}
